"use client";
import cn from "classnames";
import { useRef } from "react";
import ActiveAnchor from "@/components/common/ActiveAnchor";
import Button from "@/components/common/Button";
import { ButtonSize } from "@/constants/button";
import { useSubmenuPosition } from "../../../hooks/useSubmenuPosition";
import { type MultiLevelNavBarItem } from "../../../types";
import { isPageNavItem } from "../../../utils";
import { useNav } from "../context/NavContext";
import NavItemsList from "../NavItemsList";
import Icon from "./Icon";
import s from "./NavItem.module.scss";

export interface NavItemProps {
  item: MultiLevelNavBarItem;
  level?: number;
}

export default function NavItem(props: NavItemProps) {
  const { item, level = 0 } = props;
  const { closeMenu, setCloseMenu, enableMenuTitle } = useNav();
  const submenuRef = useRef<HTMLUListElement>(null);
  useSubmenuPosition(submenuRef, [level, item, closeMenu]);
  const isTopLevel = level === 0;
  const itemTopClassName = isTopLevel && "informa-multi-level-nav-top-item";

  if (isPageNavItem(item)) {
    if (item.displayAsCTA) {
      return (
        <div className={cn(s.anchorAsButton, itemTopClassName)}>
          <Button
            href={item.url}
            openInNewTab={item.openInNewTab}
            size={ButtonSize.Small}
          >
            {item.label}
          </Button>
        </div>
      );
    }

    return (
      <ActiveAnchor
        href={item.url}
        openInNewTab={item.openInNewTab}
        className={cn(isTopLevel ? s.anchor : s.nestedAnchor, itemTopClassName)}
        onClick={() => {
          setCloseMenu(true);
        }}
      >
        {item.label}
      </ActiveAnchor>
    );
  }

  return (
    <div
      className={cn(
        isTopLevel ? s.groupItem : s.nestedGroupItem,
        itemTopClassName,
        {
          [s.disableHover]: closeMenu,
        },
      )}
    >
      <span className={s.groupItemLabel}>
        {item.label}
        <Icon direction={isTopLevel ? "bottom" : "right"} />
      </span>

      <NavItemsList
        items={item.subMenuItems}
        heading={enableMenuTitle && isTopLevel ? item.label : undefined}
        level={level + 1}
        className={cn(isTopLevel ? s.subMenu : s.nestedSubMenu, {
          [s.closeSubMenu]: closeMenu,
        })}
        ref={submenuRef}
      />
    </div>
  );
}
